// ***************************************
// tgfStatus
//
// This object deals with the player status. 
// It uses and instance of TGFStorage to store and retrieve the data
//
// Copyright 2015-2016 Tingly Games B.V.
// Last Changed: 11-1-2016
// ***************************************
import { tgfFunctions } from "./tgfFunctions";
import { tgfSettings } from "./tgfSettings";
import { TGFStorage } from "./TGFStorage";
tgfSettings.statusDefaults = {
    preferences: {},
    version: {},
    global: {
        totalPlayed: 0,
        totalWon: 0,
        totalLost: 0,
        lastLevelPlayed: -1,
        lastLevelWon: -1,
        lastLevelLost: -1,
        maxLevelPlayed: -1,
        maxLevelWon: -1,
        maxLevelLost: -1,
        lastModePlayed: -1,
        lastModeWon: -1,
        lastModeLost: -1,
        maxModePlayed: -1,
        maxModeWon: -1,
        maxModeLost: -1,
        sumMaxScore: 0,
        sumMaxTime: 0,
        sumMaxStars: 0,
        sumMaxMoves: 0
    },
    play: {
        unlocked: false,
        totalPlayed: 0,
        totalWon: 0,
        totalLost: 0,
        lastScore: 0,
        sumScore: 0,
        minScore: 0,
        maxScore: 0,
        lastTime: 0,
        sumTime: 0,
        minTime: 0,
        maxTime: 0,
        lastStars: 0,
        sumStars: 0,
        minStars: 0,
        maxStars: 0,
        lastMoves: 0,
        sumMoves: 0,
        minMoves: 0,
        maxMoves: 0
    },
    additional: { // Must be string properties!!!
    }
};
var TGFStatus = /** @class */ (function () {
    function TGFStatus() {
    }
    // ***************************************
    // Initialization
    // ***************************************
    // Initialize the status (this can be asynchronous)
    TGFStatus.prototype.initialize = function (settings, status, onSuccess, onFail) {
        var _this = this;
        // Initialize the storage object and set the default data
        this.storage = new TGFStorage("tgfStatus");
        this.storage.initialize(settings);
        // Create the default data values, also from status
        this.setPreferencesData(tgfFunctions.mergeValues(tgfSettings.statusDefaults.preferences, status.preferences));
        this.setVersionData(tgfFunctions.mergeValues(tgfSettings.statusDefaults.version, status.version));
        this.setGlobalData(tgfFunctions.mergeValues(tgfSettings.statusDefaults.global, status.global));
        this.setAdditionalData(tgfFunctions.mergeValues(tgfSettings.statusDefaults.additional, status.additional));
        this.playDefaults = tgfFunctions.mergeValues(tgfSettings.statusDefaults.play, status.play);
        // Create the timer to check whether initialization is ready
        this.initComplete = false;
        this.initFailed = false;
        this.initOnSuccess = onSuccess;
        this.initOnFail = onFail;
        this.initState = 0;
        this.initTimer = window.setInterval(function () { _this.checkInitState.call(_this); }, 50);
    };
    // Check the initialization state
    TGFStatus.prototype.checkInitState = function () {
        "use strict";
        if (this.initState === 0) { // Waiting for storage to be initialized
            if (this.storage.initComplete) {
                this.loadStatus(true);
                this.initState = 1;
            }
            else if (this.storage.initFailed) {
                this.initFailed = true;
                this.initState = 2;
            }
        }
        if (this.initState === 1) { // Waiting for status data to load data
            if (this.loadComplete) {
                this.initComplete = true;
                this.initState = 2;
            }
            else if (this.loadFailed) {
                this.initFailed = true;
                this.initState = 2;
            }
        }
        if (this.initState === 2) { // Ready
            window.clearInterval(this.initTimer);
            if (this.initComplete && this.initOnSuccess) {
                this.initOnSuccess();
            }
            if (this.initFailed && this.initOnFail) {
                this.initOnFail();
            }
        }
    };
    // Update the storage when the client id changed (e.g. due to logging in) (called by tgfProfile)
    TGFStatus.prototype.updateId = function (newMethod) {
        "use strict";
        this.storage.updateId(newMethod);
    };
    // ***************************************
    // Loading and saving the status data
    // ***************************************
    // Load the status data
    TGFStatus.prototype.loadStatus = function (force) {
        "use strict";
        var _this = this;
        this.loadComplete = false;
        this.loadFailed = false;
        this.storage.loadData(function () { _this.loadComplete = true; }, function () { _this.loadFailed = true; }, force);
    };
    // Save the status data
    TGFStatus.prototype.saveStatus = function (force) {
        "use strict";
        var _this = this;
        this.saveComplete = false;
        this.saveFailed = false;
        this.storage.saveData(function () { _this.saveComplete = true; }, function () { _this.saveFailed = true; }, force);
    };
    // ***************************************
    // Getting and setting data
    // ***************************************
    // Get the preference data in an object
    TGFStatus.prototype.getPreferencesData = function () {
        "use strict";
        return {
            language: this.storage.getValueSimple("Pla"),
            effectsMuted: this.storage.getValueSimple("Pem"),
            effectsGain: this.storage.getValueSimple("Peg"),
            musicMuted: this.storage.getValueSimple("Pmm"),
            musicGain: this.storage.getValueSimple("Pmg")
        };
    };
    // Set the preference data from an object
    TGFStatus.prototype.setPreferencesData = function (data, save) {
        "use strict";
        if (data.language !== undefined) {
            this.storage.setValueSimple("Pla", data.language);
        }
        if (data.effectsMuted !== undefined) {
            this.storage.setValueSimple("Pem", data.effectsMuted);
        }
        if (data.effectsGain !== undefined) {
            this.storage.setValueSimple("Peg", data.effectsGain);
        }
        if (data.musicMuted !== undefined) {
            this.storage.setValueSimple("Pmm", data.musicMuted);
        }
        if (data.musicGain !== undefined) {
            this.storage.setValueSimple("Pmg", data.musicGain);
        }
        if (save) {
            this.saveStatus(false);
        }
    };
    // Get the version data in an object
    TGFStatus.prototype.getVersionData = function () {
        "use strict";
        return {
            gameId: this.storage.getValueSimple("Vgi"),
            themeId: this.storage.getValueSimple("Vti"),
            clientId: this.storage.getValueSimple("Vci"),
            gameVersion: this.storage.getValueSimple("Vgv"),
            themeVersion: this.storage.getValueSimple("Vtv"),
            clientVersion: this.storage.getValueSimple("Vcv")
        };
    };
    // Set the version data from an object
    TGFStatus.prototype.setVersionData = function (data, save) {
        "use strict";
        if (data.gameId !== undefined) {
            this.storage.setValueSimple("Vgi", data.gameId);
        }
        if (data.themeId !== undefined) {
            this.storage.setValueSimple("Vti", data.themeId);
        }
        if (data.clientId !== undefined) {
            this.storage.setValueSimple("Vci", data.clientId);
        }
        if (data.gameVersion !== undefined) {
            this.storage.setValueSimple("Vgv", data.gameVersion);
        }
        if (data.themeVersion !== undefined) {
            this.storage.setValueSimple("Vtv", data.themeVersion);
        }
        if (data.clientVersion !== undefined) {
            this.storage.setValueSimple("Vcv", data.clientVersion);
        }
        if (save) {
            this.saveStatus(false);
        }
    };
    // Get the global data in an object
    TGFStatus.prototype.getGlobalData = function () {
        "use strict";
        return {
            totalPlayed: this.storage.getValueSimple("Gtp"),
            totalWon: this.storage.getValueSimple("Gtw"),
            totalLost: this.storage.getValueSimple("Gtl"),
            lastLevelPlayed: this.storage.getValueSimple("Gllp"),
            lastLevelWon: this.storage.getValueSimple("Gllw"),
            lastLevelLost: this.storage.getValueSimple("Glll"),
            maxLevelPlayed: this.storage.getValueSimple("Gmlp"),
            maxLevelWon: this.storage.getValueSimple("Gmlw"),
            maxLevelLost: this.storage.getValueSimple("Gmll"),
            lastModePlayed: this.storage.getValueSimple("Glmp"),
            lastModeWon: this.storage.getValueSimple("Glmw"),
            lastModeLost: this.storage.getValueSimple("Glml"),
            maxModePlayed: this.storage.getValueSimple("Gmmp"),
            maxModeWon: this.storage.getValueSimple("Gmmw"),
            maxModeLost: this.storage.getValueSimple("Gmml"),
            sumMaxScore: this.storage.getValueSimple("Gsms"),
            sumMaxTime: this.storage.getValueSimple("Gsmt"),
            sumMaxStars: this.storage.getValueSimple("Gsmr"),
            sumMaxMoves: this.storage.getValueSimple("Gsmm")
        };
    };
    // Set the global data from an object
    TGFStatus.prototype.setGlobalData = function (data, save) {
        "use strict";
        if (data.totalPlayed !== undefined) {
            this.storage.setValueSimple("Gtp", data.totalPlayed);
        }
        if (data.totalWon !== undefined) {
            this.storage.setValueSimple("Gtw", data.totalWon);
        }
        if (data.totalLost !== undefined) {
            this.storage.setValueSimple("Gtl", data.totalLost);
        }
        if (data.lastLevelPlayed !== undefined) {
            this.storage.setValueSimple("Gllp", data.lastLevelPlayed);
        }
        if (data.lastLevelWon !== undefined) {
            this.storage.setValueSimple("Gllw", data.lastLevelWon);
        }
        if (data.lastLevelLost !== undefined) {
            this.storage.setValueSimple("Glll", data.lastLevelLost);
        }
        if (data.maxLevelPlayed !== undefined) {
            this.storage.setValueSimple("Gmlp", data.maxLevelPlayed);
        }
        if (data.maxLevelWon !== undefined) {
            this.storage.setValueSimple("Gmlw", data.maxLevelWon);
        }
        if (data.maxLevelLost !== undefined) {
            this.storage.setValueSimple("Gmll", data.maxLevelLost);
        }
        if (data.lastModePlayed !== undefined) {
            this.storage.setValueSimple("Glmp", data.lastModePlayed);
        }
        if (data.lastModeWon !== undefined) {
            this.storage.setValueSimple("Glmw", data.lastModeWon);
        }
        if (data.lastModeLost !== undefined) {
            this.storage.setValueSimple("Glml", data.lastModeLost);
        }
        if (data.maxModePlayed !== undefined) {
            this.storage.setValueSimple("Gmmp", data.maxModePlayed);
        }
        if (data.maxModeWon !== undefined) {
            this.storage.setValueSimple("Gmmw", data.maxModeWon);
        }
        if (data.maxModeLost !== undefined) {
            this.storage.setValueSimple("Gmml", data.maxModeLost);
        }
        if (data.sumMaxScore !== undefined) {
            this.storage.setValueSimple("Gsms", data.sumMaxScore);
        }
        if (data.sumMaxTime !== undefined) {
            this.storage.setValueSimple("Gsmt", data.sumMaxTime);
        }
        if (data.sumMaxStars !== undefined) {
            this.storage.setValueSimple("Gsmr", data.sumMaxStars);
        }
        if (data.sumMaxMoves !== undefined) {
            this.storage.setValueSimple("Gsmm", data.sumMaxMoves);
        }
        if (save) {
            this.saveStatus(false);
        }
    };
    // Get the play data in an object
    TGFStatus.prototype.getPlayData = function (mode, level) {
        "use strict";
        var playStr;
        if (mode === undefined) {
            mode = 0;
        }
        if (level === undefined) {
            level = 0;
        }
        playStr = "M" + mode + "L" + level;
        if (!this.storage.keyExists(playStr + "ex")) {
            return tgfFunctions.copyValue(this.playDefaults);
        }
        return {
            unlocked: this.storage.getValueSimple(playStr + "un"),
            totalPlayed: this.storage.getValueSimple(playStr + "tp"),
            totalWon: this.storage.getValueSimple(playStr + "tw"),
            totalLost: this.storage.getValueSimple(playStr + "tl"),
            lastScore: this.storage.getValueSimple(playStr + "las"),
            sumScore: this.storage.getValueSimple(playStr + "sus"),
            minScore: this.storage.getValueSimple(playStr + "mis"),
            maxScore: this.storage.getValueSimple(playStr + "mas"),
            lastTime: this.storage.getValueSimple(playStr + "lat"),
            sumTime: this.storage.getValueSimple(playStr + "sut"),
            minTime: this.storage.getValueSimple(playStr + "mit"),
            maxTime: this.storage.getValueSimple(playStr + "mat"),
            lastStars: this.storage.getValueSimple(playStr + "lar"),
            sumStars: this.storage.getValueSimple(playStr + "sur"),
            minStars: this.storage.getValueSimple(playStr + "mir"),
            maxStars: this.storage.getValueSimple(playStr + "mar"),
            lastMoves: this.storage.getValueSimple(playStr + "lam"),
            sumMoves: this.storage.getValueSimple(playStr + "sum"),
            minMoves: this.storage.getValueSimple(playStr + "mim"),
            maxMoves: this.storage.getValueSimple(playStr + "mam")
        };
    };
    // Set the play data from an object
    TGFStatus.prototype.setPlayData = function (mode, level, data, save) {
        "use strict";
        var playStr;
        if (mode === undefined) {
            mode = 0;
        }
        if (level === undefined) {
            level = 0;
        }
        playStr = "M" + mode + "L" + level;
        this.storage.setValueSimple(playStr + "ex", true);
        if (data.unlocked !== undefined) {
            this.storage.setValueSimple(playStr + "un", data.unlocked);
        }
        if (data.totalPlayed !== undefined) {
            this.storage.setValueSimple(playStr + "tp", data.totalPlayed);
        }
        if (data.totalWon !== undefined) {
            this.storage.setValueSimple(playStr + "tw", data.totalWon);
        }
        if (data.totalLost !== undefined) {
            this.storage.setValueSimple(playStr + "tl", data.totalLost);
        }
        if (data.lastScore !== undefined) {
            this.storage.setValueSimple(playStr + "las", data.lastScore);
        }
        if (data.sumScore !== undefined) {
            this.storage.setValueSimple(playStr + "sus", data.sumScore);
        }
        if (data.minScore !== undefined) {
            this.storage.setValueSimple(playStr + "mis", data.minScore);
        }
        if (data.maxScore !== undefined) {
            this.storage.setValueSimple(playStr + "mas", data.maxScore);
        }
        if (data.lastTime !== undefined) {
            this.storage.setValueSimple(playStr + "lat", data.lastTime);
        }
        if (data.sumTime !== undefined) {
            this.storage.setValueSimple(playStr + "sut", data.sumTime);
        }
        if (data.minTime !== undefined) {
            this.storage.setValueSimple(playStr + "mit", data.minTime);
        }
        if (data.maxTime !== undefined) {
            this.storage.setValueSimple(playStr + "mat", data.maxTime);
        }
        if (data.lastStars !== undefined) {
            this.storage.setValueSimple(playStr + "lar", data.lastStars);
        }
        if (data.sumStars !== undefined) {
            this.storage.setValueSimple(playStr + "sur", data.sumStars);
        }
        if (data.minStars !== undefined) {
            this.storage.setValueSimple(playStr + "mir", data.minStars);
        }
        if (data.maxStars !== undefined) {
            this.storage.setValueSimple(playStr + "mar", data.maxStars);
        }
        if (data.lastMoves !== undefined) {
            this.storage.setValueSimple(playStr + "lam", data.lastMoves);
        }
        if (data.sumMoves !== undefined) {
            this.storage.setValueSimple(playStr + "sum", data.sumMoves);
        }
        if (data.minMoves !== undefined) {
            this.storage.setValueSimple(playStr + "mim", data.minMoves);
        }
        if (data.maxMoves !== undefined) {
            this.storage.setValueSimple(playStr + "mam", data.maxMoves);
        }
        if (save) {
            this.saveStatus(false);
        }
    };
    // Get the additional data in an object
    TGFStatus.prototype.getAdditionalData = function () {
        "use strict";
        return this.storage.getValue("Additional");
    };
    // Set the additional data from an object
    TGFStatus.prototype.setAdditionalData = function (data, save) {
        "use strict";
        this.storage.setValue("Additional", data);
        if (save) {
            this.saveStatus(false);
        }
    };
    // ***************************************
    // Changing status, saving corresponding data
    // ***************************************
    // Set the language
    TGFStatus.prototype.setLanguage = function (language) {
        "use strict";
        var preferencesData = this.getPreferencesData();
        if (language !== undefined) {
            preferencesData.language = language;
        }
        // Now save it
        this.setPreferencesData(preferencesData);
        this.saveStatus(false);
    };
    // Set the audio data
    TGFStatus.prototype.setAudio = function (effectsMuted, effectsGain, musicMuted, musicGain) {
        "use strict";
        var preferencesData = this.getPreferencesData();
        if (effectsMuted !== undefined) {
            preferencesData.effectsMuted = effectsMuted;
        }
        if (effectsGain !== undefined) {
            preferencesData.effectsGain = effectsGain;
        }
        if (musicMuted !== undefined) {
            preferencesData.musicMuted = musicMuted;
        }
        if (musicGain !== undefined) {
            preferencesData.musicGain = musicGain;
        }
        // Now save it
        this.setPreferencesData(preferencesData);
        this.saveStatus(false);
    };
    // Start a play
    TGFStatus.prototype.startPlay = function (mode, level) {
        "use strict";
        var versionData = this.getVersionData(), globalData = this.getGlobalData(), playData = this.getPlayData(mode, level);
        this.theLevel = level === undefined ? 0 : level;
        this.theMode = mode === undefined ? 0 : mode;
        // Version information
        if (tgfSettings.game && tgfSettings.game.id) {
            versionData.gameId = tgfSettings.game.id;
        }
        if (tgfSettings.theme && tgfSettings.theme.id) {
            versionData.themeId = tgfSettings.theme.id;
        }
        if (tgfSettings.client && tgfSettings.client.id) {
            versionData.clientId = tgfSettings.client.id;
        }
        if (tgfSettings.game && tgfSettings.game.version) {
            versionData.gameVersion = tgfSettings.game.version;
        }
        if (tgfSettings.theme && tgfSettings.theme.version) {
            versionData.themeVersion = tgfSettings.theme.version;
        }
        if (tgfSettings.client && tgfSettings.client.version) {
            versionData.clientVersion = tgfSettings.client.version;
        }
        // Global information
        globalData.totalPlayed += 1;
        globalData.lastLevelPlayed = this.theLevel;
        globalData.maxLevelPlayed = Math.max(globalData.maxLevelPlayed, this.theLevel);
        globalData.lastModePlayed = this.theMode;
        globalData.maxModePlayed = Math.max(globalData.maxModePlayed, this.theMode);
        // Play information
        playData.totalPlayed += 1;
        // Now save it
        this.setVersionData(versionData);
        this.setGlobalData(globalData);
        this.setPlayData(mode, level, playData);
        this.saveStatus(false);
    };
    // End a play by winning
    TGFStatus.prototype.endPlayWon = function (score, time, stars, moves) {
        "use strict";
        var globalData = this.getGlobalData(), playData = this.getPlayData(this.theMode, this.theLevel);
        if (score === undefined) {
            score = 0;
        }
        if (time === undefined) {
            time = 0;
        }
        if (stars === undefined) {
            stars = 0;
        }
        if (moves === undefined) {
            moves = 0;
        }
        // Global information
        globalData.totalWon += 1;
        globalData.lastLevelWon = this.theLevel;
        globalData.maxLevelWon = Math.max(globalData.maxLevelWon, this.theLevel);
        globalData.lastModeWon = this.theMode;
        globalData.maxModeWon = Math.max(globalData.maxModeWon, this.theMode);
        if (score > playData.maxScore) {
            globalData.sumMaxScore += score - playData.maxScore;
        }
        if (time > playData.maxTime) {
            globalData.sumMaxTime += time - playData.maxTime;
        }
        if (stars > playData.maxStars) {
            globalData.sumMaxStars += stars - playData.maxStars;
        }
        if (moves > playData.maxMoves) {
            globalData.sumMaxMoves += moves - playData.maxMoves;
        }
        // Play information
        playData.totalWon += 1;
        playData.lastScore = score;
        playData.sumScore += score;
        playData.minScore = playData.totalWon === 1 ? score : Math.min(playData.minScore, score);
        playData.maxScore = playData.totalWon === 1 ? score : Math.max(playData.maxScore, score);
        playData.lastTime = time;
        playData.sumTime += time;
        playData.minTime = playData.totalWon === 1 ? time : Math.min(playData.minTime, time);
        playData.maxTime = playData.totalWon === 1 ? time : Math.max(playData.maxTime, time);
        playData.lastStars = stars;
        playData.sumStars += stars;
        playData.minStars = playData.totalWon === 1 ? stars : Math.min(playData.minStars, stars);
        playData.maxStars = playData.totalWon === 1 ? stars : Math.max(playData.maxStars, stars);
        playData.lastMoves = moves;
        playData.sumMoves += moves;
        playData.minMoves = playData.totalWon === 1 ? moves : Math.min(playData.minMoves, moves);
        playData.maxMoves = playData.totalWon === 1 ? moves : Math.max(playData.maxMoves, moves);
        // Now save the data
        this.setGlobalData(globalData);
        this.setPlayData(this.theMode, this.theLevel, playData);
        this.saveStatus(false);
    };
    // End a play by losing
    TGFStatus.prototype.endPlayLost = function () {
        "use strict";
        var globalData = this.getGlobalData(), playData = this.getPlayData(this.theMode, this.theLevel);
        // Global information
        globalData.totalLost += 1;
        globalData.lastLevelLost = this.theLevel;
        globalData.maxLevelLost = Math.max(globalData.maxLevelLost, this.theLevel);
        globalData.lastModeLost = this.theMode;
        globalData.maxModeLost = Math.max(globalData.maxModeLost, this.theMode);
        // Play information
        playData.totalLost += 1;
        // Now save the data
        this.setGlobalData(globalData);
        this.setPlayData(this.theMode, this.theLevel, playData);
        this.saveStatus(false);
    };
    // Unlock a level
    TGFStatus.prototype.unlockPlay = function (mode, level) {
        "use strict";
        var playData = this.getPlayData(mode, level);
        playData.unlocked = true;
        // Now save the data
        this.setPlayData(mode, level, playData);
        this.saveStatus(false);
    };
    return TGFStatus;
}());
// ***************************************
// Create the default version
// ***************************************
export var tgfStatus = new TGFStatus();
